'use client';

import { ThemeNameType } from "@/tailwind-config/theme.constants";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { CarouselTimelineItem, CarouselTimelineItemProps } from "../CarouselTimelineItem";
import Grid from "../Grid";
import { TextCombination } from "../TextCombination";
import ThemeWrapper from "../ThemeWrapper";
import { useCarouselTimer } from "./CarouselRotation.hooks";

export type CarouselRotationProps = {
  eyebrow?: string;
  title: string;
  description?: string;
  theme?: ThemeNameType;
  items: {
    eyebrow: CarouselTimelineItemProps['eyebrow'];
    title: CarouselTimelineItemProps['title'];
    imageSrc: CarouselTimelineItemProps['imageSrc'];
    description?: CarouselTimelineItemProps['description'];
    calloutTitle?: CarouselTimelineItemProps['calloutTitle'];
    calloutDescription?: CarouselTimelineItemProps['calloutDescription'];
  }[];
  autoplayInterval?: number;
}

/**
 * Carousel Rotation is a timer-based carousel that plays until an item is clicked. 
 * However, it will only start playing when the component is scrolled into view.
 * The interval is configurable via the autoplayInterval prop, and defaults to 12 seconds.
 * 
 * On mobile, the timer is disabled entirely, and only clicking advances the carousel.
 */
export const CarouselRotation = ({ theme, eyebrow, title, description, items, autoplayInterval }: CarouselRotationProps) => {
  const { ref, activeFeatureIndex, updateActiveFeatureIndex, isTimerActive, setIsTimerActive } = useCarouselTimer(items.length ?? 0, autoplayInterval);

  return (
    <ThemeWrapper theme={theme || 'theme-white'}>
      <div className="surface-l0 py-margin-section-md" ref={ref}>
        <Grid>
          <div className="col-span-4 md:col-span-12 pb-margin-section-md">
            <TextCombination type="d2p1" align="center" headingLevel="h1" eyebrow={eyebrow} title={title} description={description} />
          </div>
          <div className="col-span-4 md:col-span-5 flex flex-col gap-y-lg md:gap-y-0">
            {items.map((item, index) => (
              <CarouselTimelineItem
                key={item.title}
                {...item}
                onClick={() => {
                  updateActiveFeatureIndex(index);
                  setIsTimerActive(false);
                }}
                isActive={activeFeatureIndex === index}
                isTimerActive={isTimerActive}
                timerDuration={autoplayInterval}
              />
            ))}
          </div>
          <div className="hidden md:block col-span-4 md:col-span-6 md:col-start-7 relative">
            <AnimatePresence>
              <motion.div
                key={`carousel-rotation-item-${activeFeatureIndex}`}
                className="overflow-hidden rounded-10 absolute inset-0 flex flex-col justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <Image
                  className="w-full object-cover"
                  /*
                   * If the activeFeatureIndex is null, we're waiting for the component to be in view - 
                   * show the first item as the default state
                   */
                  src={items[activeFeatureIndex ?? 0].imageSrc}
                  alt={items[activeFeatureIndex ?? 0].title}
                  width={500}
                  height={500} />
              </motion.div>
            </AnimatePresence>

          </div>
        </Grid>
      </div>
    </ThemeWrapper>
  )
}
